import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, User, Mail, FileText, Video, Clock, CheckCircle2,
  XCircle, Eye, Target, Sparkles, ExternalLink, Loader2, Play,
  Search, StickyNote, Calendar, CheckSquare, Square, X,
  ChevronDown, ChevronUp, LayoutGrid, List, Filter, Briefcase,
  MapPin, Download, Share2, MessageSquare, XOctagon
} from 'lucide-react';
import { InterviewScheduleModal } from '../components/InterviewScheduleModal';
import { JobSyndicationPanel } from '../components/JobSyndicationPanel';

const STATUS_OPTIONS = [
  { value: 'pending',     label: 'Pending',     color: 'bg-amber-100 text-amber-700' },
  { value: 'reviewed',    label: 'Reviewed',    color: 'bg-blue-100 text-blue-700' },
  { value: 'shortlisted', label: 'Shortlisted', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'interview',   label: 'Interview',   color: 'bg-indigo-100 text-indigo-700' },
  { value: 'hired',       label: 'Hired',       color: 'bg-purple-100 text-purple-700' },
  { value: 'rejected',    label: 'Rejected',    color: 'bg-red-100 text-red-700' },
];

const getStatusMeta = (status) => STATUS_OPTIONS.find(s => s.value === status) || { label: status, color: 'bg-gray-100 text-gray-600' };

export default function ApplicantsPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();

  const [job, setJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [showVideo, setShowVideo] = useState({});

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Selection & bulk
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  // Notes panel
  const [activeNoteApp, setActiveNoteApp] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [notesMap, setNotesMap] = useState({}); // appId -> count

  // Interview scheduling
  const [schedulingApp, setSchedulingApp] = useState(null);
  const [showSyndication, setShowSyndication] = useState(false);

  // Expanded cover letters
  const [expandedCL, setExpandedCL] = useState({});

  // AI shortlisting
  const [shortlisting, setShortlisting] = useState(false);
  const shortlistCredits = user?.service_credits?.candidate_shortlist || 0;

  useEffect(() => { fetchData(); }, [jobId]);

  const fetchData = async () => {
    try {
      const [jobRes, applicantsRes] = await Promise.all([
        api.get(`/jobs/${jobId}`),
        api.get(`/applications/job/${jobId}`)
      ]);
      setJob(jobRes.data);
      setApplicants(applicantsRes.data);
    } catch {
      toast.error('Failed to load applicants');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (applicationId, status, extra = {}) => {
    setUpdatingStatus(applicationId);
    try {
      await api.put(`/applications/${applicationId}/status`, { status, ...extra });
      setApplicants(prev => prev.map(app =>
        app.id === applicationId ? { ...app, status, ...extra } : app
      ));
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // ---- Bulk ----
  const toggleSelect = (id) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const selectAll = () => setSelected(new Set(filteredApplicants.map(a => a.id)));
  const clearSelection = () => setSelected(new Set());

  const applyBulk = async () => {
    if (!bulkStatus || selected.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.put('/applications/bulk-status', {
        application_ids: [...selected],
        status: bulkStatus
      });
      toast.success(`Updated ${res.data.updated} applications`);
      setSelected(new Set()); setBulkStatus(''); fetchData();
    } catch { toast.error('Bulk update failed'); }
    finally { setBulkLoading(false); }
  };

  // ---- Notes ----
  const openNotes = async (app) => {
    setActiveNoteApp(app); setNotes([]); setNoteText('');
    setLoadingNotes(true);
    try {
      const res = await api.get(`/applications/${app.id}/notes`);
      setNotes(res.data);
      setNotesMap(prev => ({ ...prev, [app.id]: res.data.length }));
    } catch {}
    finally { setLoadingNotes(false); }
  };

  const submitNote = async () => {
    if (!noteText.trim() || !activeNoteApp) return;
    setSavingNote(true);
    try {
      const res = await api.post(`/applications/${activeNoteApp.id}/notes`, { text: noteText.trim() });
      setNotes(prev => [...prev, res.data]);
      setNoteText('');
      setNotesMap(prev => ({ ...prev, [activeNoteApp.id]: (prev[activeNoteApp.id] || 0) + 1 }));
    } catch { toast.error('Failed to save note'); }
    finally { setSavingNote(false); }
  };

  // ---- Message candidate ----
  const messageCandidate = (app) => {
    const recipientId  = app.applicant_id || app.applicant?.id;
    const contextLabel = `${app.job_title || job?.title || 'Job'} application`;
    navigate(`/messages?with=${recipientId}&context=${encodeURIComponent(contextLabel)}&context_id=${app.id}`);
  };

  // ---- Export CSV ----
  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Status', 'Applied Date', 'Experience (yrs)', 'Skills', 'AI Score'];
    const rows = filteredApplicants.map(app => [
      app.applicant?.full_name || '',
      app.applicant?.email || '',
      app.status,
      new Date(app.created_at).toLocaleDateString('en-GB'),
      app.applicant?.experience_years || '',
      (app.applicant?.skills || []).join('; '),
      app.ai_match_score || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `applicants-${job?.title || jobId}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- AI shortlisting ----
  const runAiShortlist = async () => {
    setShortlisting(true);
    try {
      const res = await api.post(`/applications/job/${jobId}/ai-shortlist`);
      const scoreMap = {};
      res.data.shortlisted.forEach(r => { scoreMap[r.application_id] = r.score; });
      setApplicants(prev => prev.map(app => app.id in scoreMap ? { ...app, ai_match_score: scoreMap[app.id] } : app));
      setSortBy('score');
      toast.success(`Shortlisted ${res.data.shortlisted.length} applicants. ${res.data.credits_remaining} credit(s) left.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run AI shortlisting');
    } finally {
      setShortlisting(false);
    }
  };

  // ---- Interview scheduling via InterviewScheduleModal ----
  const onInterviewScheduled = (updatedApp) => {
    setApplicants(prev => prev.map(a => a.id === updatedApp.id ? { ...a, ...updatedApp } : a));
    setSchedulingApp(null);
  };

  // ---- Filter & Sort ----
  const filteredApplicants = applicants
    .filter(app => {
      const matchStatus = statusFilter === 'all' || app.status === statusFilter;
      const q = search.toLowerCase();
      const matchSearch = !q ||
        (app.applicant?.full_name || '').toLowerCase().includes(q) ||
        (app.applicant?.email || '').toLowerCase().includes(q) ||
        (app.applicant?.skills || []).some(s => s.toLowerCase().includes(q));
      return matchStatus && matchSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === 'score') return (b.ai_match_score || 0) - (a.ai_match_score || 0);
      return 0;
    });

  const statusCounts = applicants.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1; return acc;
  }, {});

  if (loading) return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32" />
        {[1,2,3].map(i => <Skeleton key={i} className="h-40" />)}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/employer/dashboard')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
            <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-purple-200 text-purple-700 hover:bg-purple-50"
              onClick={() => shortlistCredits > 0 ? runAiShortlist() : navigate('/services')}
              disabled={shortlisting || applicants.length === 0}
              title={shortlistCredits > 0 ? `${shortlistCredits} credit(s) available` : 'Requires an AI Candidate Shortlisting credit'}
            >
              {shortlisting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              {shortlistCredits > 0 ? 'Run AI Shortlisting' : 'Buy AI Shortlisting'}
            </Button>
            <Button variant="outline" size="sm" className="border-gray-300" onClick={() => setShowSyndication(true)}>
              <Share2 className="w-4 h-4 mr-1.5" /> Syndicate
            </Button>
            <Link to="/employer/pipeline">
              <Button variant="outline" size="sm" className="border-[#003366] text-[#003366]">
                <LayoutGrid className="w-4 h-4 mr-1.5" /> Pipeline
              </Button>
            </Link>
          </div>
        </div>

        {/* Job Header */}
        {job && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h1 className="text-2xl font-bold">{job.title}</h1>
                  <p className="text-muted-foreground">{job.company_name} · {job.location}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-[#003366]">{applicants.length}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                  {STATUS_OPTIONS.slice(0, 4).map(s => (
                    <div key={s.value} className="text-center">
                      <p className="text-xl font-bold text-foreground">{statusCounts[s.value] || 0}</p>
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters & Bulk */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-48">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input className="pl-8 h-9 text-sm" placeholder="Search by name, email, skills…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-40 text-sm">
                  <Filter className="w-3.5 h-3.5 mr-1 text-muted-foreground" />
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label} {statusCounts[s.value] ? `(${statusCounts[s.value]})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-9 w-36 text-sm">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="score">AI score</SelectItem>
                </SelectContent>
              </Select>

              {/* Bulk controls */}
              {filteredApplicants.length > 0 && (
                <button
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  onClick={selected.size === filteredApplicants.length ? clearSelection : selectAll}
                >
                  {selected.size === filteredApplicants.length
                    ? <><CheckSquare className="w-4 h-4 text-[#003366]" /> Deselect all</>
                    : <><Square className="w-4 h-4" /> Select all</>
                  }
                </button>
              )}

              {selected.size > 0 && (
                <>
                  <span className="text-xs bg-[#003366] text-white px-2 py-1 rounded-full font-medium">{selected.size} selected</span>
                  {/* Quick reject all */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-9 border-red-200 text-red-600 hover:bg-red-50 text-xs"
                    onClick={() => { setBulkStatus('rejected'); setTimeout(applyBulk, 50); }}
                    disabled={bulkLoading}
                  >
                    <XOctagon className="w-3.5 h-3.5 mr-1" />Reject All
                  </Button>
                  <Select value={bulkStatus} onValueChange={setBulkStatus}>
                    <SelectTrigger className="h-9 w-36 text-sm">
                      <SelectValue placeholder="Move to…" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="h-9 bg-[#003366]" onClick={applyBulk} disabled={!bulkStatus || bulkLoading}>
                    {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                  <button className="text-muted-foreground hover:text-foreground" onClick={clearSelection}>
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}

              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{filteredApplicants.length} shown</span>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={exportCSV}>
                  <Download className="w-3.5 h-3.5 mr-1" />Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Applicants List */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Applicants</CardTitle>
            <CardDescription>Review and manage candidates for this position</CardDescription>
          </CardHeader>
          <CardContent className="p-4">
            {filteredApplicants.length === 0 ? (
              <div className="text-center py-12">
                <User className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="font-medium mb-1">No applicants found</p>
                <p className="text-sm text-muted-foreground">
                  {applicants.length === 0 ? 'No one has applied yet.' : 'Try adjusting your filters.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredApplicants.map(app => {
                  const sm = getStatusMeta(app.status);
                  const isSelected = selected.has(app.id);
                  const noteCount = notesMap[app.id] || 0;
                  return (
                    <div
                      key={app.id}
                      className={`p-5 rounded-xl border transition-all ${isSelected ? 'border-[#003366] bg-[#003366]/5' : 'hover:border-[#003366]/30 bg-white'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        {/* Left: checkbox + avatar + info */}
                        <div className="flex items-start gap-3">
                          <button
                            className="mt-1 text-muted-foreground hover:text-[#003366] transition-colors"
                            onClick={() => toggleSelect(app.id)}
                          >
                            {isSelected ? <CheckSquare className="w-4 h-4 text-[#003366]" /> : <Square className="w-4 h-4" />}
                          </button>
                          <div className="w-12 h-12 rounded-full bg-[#003366] flex items-center justify-center shrink-0">
                            <span className="text-white text-lg font-bold">
                              {(app.applicant?.full_name || 'U').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-base">{app.applicant?.full_name || 'Unknown'}</h3>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-0.5">
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />{app.applicant?.email}
                              </span>
                              {app.applicant?.experience_years && (
                                <span>{app.applicant.experience_years} yrs exp.</span>
                              )}
                            </div>

                            {/* Skills */}
                            {app.applicant?.skills?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {app.applicant.skills.slice(0, 6).map((skill, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>
                                ))}
                                {app.applicant.skills.length > 6 && (
                                  <Badge variant="outline" className="text-xs">+{app.applicant.skills.length - 6}</Badge>
                                )}
                              </div>
                            )}

                            {/* AI score */}
                            {app.ai_match_score && (
                              <div className="mt-2">
                                <Badge className="bg-purple-100 text-purple-700 border-0">
                                  <Sparkles className="w-3 h-3 mr-1" />{app.ai_match_score}% Match
                                </Badge>
                              </div>
                            )}

                            {/* Cover letter */}
                            {app.cover_letter && (
                              <div className="mt-3 p-3 bg-secondary rounded-lg">
                                <p className={`text-sm text-muted-foreground ${expandedCL[app.id] ? '' : 'line-clamp-2'}`}>
                                  {app.cover_letter}
                                </p>
                                {app.cover_letter.length > 120 && (
                                  <button
                                    className="text-xs text-[#003366] mt-1 hover:underline font-medium flex items-center gap-0.5"
                                    onClick={() => setExpandedCL(p => ({ ...p, [app.id]: !p[app.id] }))}
                                  >
                                    {expandedCL[app.id] ? <><ChevronUp className="w-3 h-3" />Show less</> : <><ChevronDown className="w-3 h-3" />Read more</>}
                                  </button>
                                )}
                              </div>
                            )}

                            {/* Interview date */}
                            {app.interview_date && (
                              <div className="mt-2 flex items-center gap-1.5 text-sm text-indigo-600 font-medium">
                                <Calendar className="w-3.5 h-3.5" />
                                Interview: {new Date(app.interview_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}

                            {/* Employer notes */}
                            {(app.notes || app.employer_notes) && (
                              <div className="mt-2 p-2.5 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-900">
                                {app.notes || app.employer_notes}
                              </div>
                            )}

                            {/* Documents */}
                            <div className="flex items-center gap-4 mt-3">
                              {app.applicant?.cv_url && (
                                <a
                                  href={`${process.env.REACT_APP_BACKEND_URL}${app.applicant.cv_url}`}
                                  target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-sm text-[#003366] hover:underline"
                                >
                                  <FileText className="w-4 h-4" />View CV <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                              {app.applicant?.video_cv_url && (
                                <button
                                  className="flex items-center gap-1 text-sm text-[#003366] hover:underline"
                                  onClick={() => setShowVideo(p => ({...p, [app.id]: !p[app.id]}))}
                                >
                                  <Play className="w-4 h-4" />{showVideo[app.id] ? 'Hide' : 'Watch'} Video CV
                                </button>
                              )}
                            </div>
                            {showVideo[app.id] && app.applicant?.video_cv_url && (
                              <video controls className="w-full mt-2 rounded-lg max-h-48 bg-black">
                                <source src={`${process.env.REACT_APP_BACKEND_URL}${app.applicant.video_cv_url}`} type="video/mp4" />
                              </video>
                            )}
                          </div>
                        </div>

                        {/* Right: status + actions */}
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <Badge className={sm.color}>{sm.label}</Badge>

                          <Select
                            value={app.status}
                            onValueChange={(v) => updateStatus(app.id, v)}
                            disabled={updatingStatus === app.id}
                          >
                            <SelectTrigger className="w-38 h-8 text-xs" data-testid={`status-select-${app.id}`}>
                              {updatingStatus === app.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <SelectValue />
                              }
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map(s => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Schedule interview */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                            onClick={() => { setSchedulingApp(app); setInterviewDate(app.interview_date ? app.interview_date.slice(0,16) : ''); setInterviewNotes(''); }}
                          >
                            <Calendar className="w-3.5 h-3.5 mr-1" />
                            {app.interview_date ? 'Reschedule' : 'Schedule'}
                          </Button>

                          {/* Message candidate */}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-xs text-[#003366] hover:bg-[#003366]/10"
                            onClick={() => messageCandidate(app)}
                          >
                            <MessageSquare className="w-3.5 h-3.5 mr-1" />Message
                          </Button>

                          {/* Notes */}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-xs text-amber-700 hover:bg-amber-50"
                            onClick={() => openNotes(app)}
                          >
                            <StickyNote className="w-3.5 h-3.5 mr-1" />
                            Notes {noteCount > 0 ? `(${noteCount})` : ''}
                          </Button>

                          <p className="text-xs text-muted-foreground mt-1">
                            Applied {new Date(app.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes side panel */}
      {activeNoteApp && (
        <div className="fixed top-0 right-0 bottom-0 w-80 bg-white border-l shadow-2xl flex flex-col z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-amber-50 shrink-0">
            <div>
              <p className="font-semibold text-sm">{activeNoteApp.applicant?.full_name}</p>
              <p className="text-xs text-muted-foreground">Internal Notes</p>
            </div>
            <button onClick={() => setActiveNoteApp(null)} className="text-muted-foreground hover:text-foreground p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {loadingNotes
              ? <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>
              : notes.length === 0
                ? <p className="text-xs text-muted-foreground italic">No notes yet.</p>
                : notes.map(note => (
                  <div key={note.id} className="bg-amber-50 border border-amber-100 rounded-lg p-2.5">
                    <p className="text-sm whitespace-pre-wrap">{note.text}</p>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                      <span className="font-medium text-amber-700">{note.author_name}</span>
                      <span>{new Date(note.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}</span>
                    </div>
                  </div>
                ))
            }
          </div>
          <div className="p-4 border-t shrink-0">
            <textarea
              className="w-full text-sm border rounded-lg p-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400"
              rows={3}
              placeholder="Add a note… (Ctrl+Enter to save)"
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submitNote(); }}
            />
            <Button
              className="mt-2 w-full bg-amber-500 hover:bg-amber-600 text-white h-9"
              onClick={submitNote}
              disabled={savingNote || !noteText.trim()}
            >
              {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <><StickyNote className="w-4 h-4 mr-1.5" />Save Note</>}
            </Button>
          </div>
        </div>
      )}

      {/* Interview scheduling modal (with calendar integration) */}
      {schedulingApp && (
        <InterviewScheduleModal
          app={schedulingApp}
          onClose={() => setSchedulingApp(null)}
          onScheduled={onInterviewScheduled}
        />
      )}

      {/* Job syndication panel */}
      {showSyndication && jobId && (
        <JobSyndicationPanel jobId={jobId} onClose={() => setShowSyndication(false)} />
      )}
    </div>
  );
}
